import React, { useState, useEffect, useCallback } from 'react'

import { toAbsoluteUrl, KTCard, KTCardBody, QUERIES } from '../../../../../../_metronic/helpers'
import { useAuth } from '../../../../auth'
import { toast } from 'react-toastify'
import { getUserById } from '../../_requests'
import { useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useQueryResponse } from '../core/QueryResponseProvider'
import UserOverviewTabs from './components/UserOverviewTabs'



const UserDetails: React.FC = () => {
    const { saveUser } = useAuth()

    const [, setRejectLoading] = useState(false)
    const [userDetails, setUserDetails] = useState<any>({})
    const { query } = useQueryResponse()
    const queryClient = useQueryClient()
    const { id } = useParams()


    const getUserDetails = useCallback(async (userId) => {
        try {
            setRejectLoading(true)
            const response = await getUserById(userId)
            setUserDetails(response || {})
            // toast.success('The user has been rejected successfully.')
            queryClient.invalidateQueries({ queryKey: [`${QUERIES.USERS_LIST}-${query}`] })
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.message || 'Something went wrong'
            toast.error(msg)
        } finally {
            setRejectLoading(false)
        }
    }, [queryClient, query])

    useEffect(() => {
        if (id) {
            getUserDetails(id)
        }
    }, [getUserDetails, id])

    const formatError = (err: any): string => {
        if (!err) return ''
        if (typeof err === 'string') return err
        if (Array.isArray(err)) return err.join(', ')
        try {
            return JSON.stringify(err)
        } catch {
            return String(err)
        }
    }

    const avatarUrl = userDetails?.user?.profile_picture_url ? userDetails?.user?.profile_picture_url : toAbsoluteUrl('/media/avatars/placeholder.png');

    return (
        <KTCard>
            <KTCardBody className='p-8 position-relative'>
                {/* Show verification status */}
                {userDetails.is_mobile_verified === 1 && (
                    <span className='font-12 fw-bold badge badge badge-light-success badge-abs left'>Email Verified</span>
                )}
                {userDetails.is_ndis_verified === 1 && userDetails.role === 'ndis_participant' && (
                    <span className='font-12 fw-bold badge badge badge-light-primary badge-abs right'>NDIS Verified</span>
                )}

                <div className='p-5 position-relative'
                    style={{
                        backgroundImage: "url('https://keenthemes.com/metronic/tailwind/react/demo1//media/images/2600x1200/bg-1.png')",
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                    }}>

                    <div className='d-flex flex-column gap-4 align-items-center'>
                        {/* Profile Image */}
                        <div className='symbol symbol-100px'>
                            <img
                                src={avatarUrl}
                                alt={userDetails.first_name}
                                className='navbar-user-icon img-theme-border'
                            />
                        </div>

                        {/* User Name */}
                        <div className="d-flex align-items-center gap-2 mt-3">
                            <h3 className="text-black fw-bold text-capitalize m-0">{userDetails?.user?.first_name} {userDetails?.user?.last_name}</h3>
                            {userDetails.approval_status === 'approved' && (
                                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none" className="text-primary">
                                    <path d="M14.5425 6.89749L13.5 5.83999C13.4273 5.76877 13.3699 5.6835 13.3312 5.58937C13.2925 5.49525 13.2734 5.39424 13.275 5.29249V3.79249C13.274 3.58699 13.2324 3.38371 13.1527 3.19432C13.0729 3.00494 12.9565 2.83318 12.8101 2.68892C12.6638 2.54466 12.4904 2.43073 12.2998 2.35369C12.1093 2.27665 11.9055 2.23801 11.7 2.23999H10.2C10.0982 2.24159 9.99722 2.22247 9.9031 2.18378C9.80898 2.1451 9.72371 2.08767 9.65249 2.01499L8.60249 0.957487C8.30998 0.665289 7.91344 0.50116 7.49999 0.50116C7.08654 0.50116 6.68999 0.665289 6.39749 0.957487L5.33999 1.99999C5.26876 2.07267 5.1835 2.1301 5.08937 2.16879C4.99525 2.20747 4.89424 2.22659 4.79249 2.22499H3.29249C3.08699 2.22597 2.88371 2.26754 2.69432 2.34731C2.50494 2.42709 2.33318 2.54349 2.18892 2.68985C2.04466 2.8362 1.93073 3.00961 1.85369 3.20013C1.77665 3.39064 1.73801 3.5945 1.73999 3.79999V5.29999C1.74159 5.40174 1.72247 5.50275 1.68378 5.59687C1.6451 5.691 1.58767 5.77627 1.51499 5.84749L0.457487 6.89749C0.165289 7.19 0.00115967 7.58654 0.00115967 7.99999C0.00115967 8.41344 0.165289 8.80998 0.457487 9.10249L1.49999 10.16C1.57267 10.2312 1.6301 10.3165 1.66878 10.4106C1.70747 10.5047 1.72659 10.6057 1.72499 10.7075V12.2075C1.72597 12.413 1.76754 12.6163 1.84731 12.8056C1.92709 12.995 2.04349 13.1668 2.18985 13.3111C2.3362 13.4553 2.50961 13.5692 2.70013 13.6463C2.89064 13.7233 3.0945 13.762 3.29999 13.76H4.79999C4.90174 13.7584 5.00275 13.7775 5.09687 13.8162C5.191 13.8549 5.27627 13.9123 5.34749 13.985L6.40499 15.0425C6.69749 15.3347 7.09404 15.4988 7.50749 15.4988C7.92094 15.4988 8.31748 15.3347 8.60999 15.0425L9.65999 14C9.73121 13.9273 9.81647 13.8699 9.9106 13.8312C10.0047 13.7925 10.1057 13.7734 10.2075 13.775H11.7075C12.1212 13.775 12.518 13.6106 12.8106 13.3181C13.1031 13.0255 13.2675 12.6287 13.2675 12.215V10.715C13.2659 10.6132 13.285 10.5122 13.3237 10.4181C13.3624 10.324 13.4198 10.2387 13.4925 10.1675L14.55 9.10999C14.6953 8.96452 14.8104 8.79176 14.8887 8.60164C14.9671 8.41152 15.007 8.20779 15.0063 8.00218C15.0056 7.79656 14.9643 7.59311 14.8847 7.40353C14.8051 7.21394 14.6888 7.04197 14.5425 6.89749ZM10.635 6.64999L6.95249 10.25C6.90055 10.3026 6.83864 10.3443 6.77038 10.3726C6.70212 10.4009 6.62889 10.4153 6.55499 10.415C6.48062 10.4139 6.40719 10.3982 6.33896 10.3685C6.27073 10.3389 6.20905 10.2961 6.15749 10.2425L4.37999 8.44249C4.32532 8.39044 4.28169 8.32793 4.25169 8.25867C4.22169 8.18941 4.20593 8.11482 4.20536 8.03934C4.20479 7.96387 4.21941 7.88905 4.24836 7.81934C4.27731 7.74964 4.31999 7.68647 4.37387 7.63361C4.42774 7.58074 4.4917 7.53926 4.56194 7.51163C4.63218 7.484 4.70726 7.47079 4.78271 7.47278C4.85816 7.47478 4.93244 7.49194 5.00112 7.52324C5.0698 7.55454 5.13148 7.59935 5.18249 7.65499L6.56249 9.05749L9.84749 5.84749C9.95296 5.74215 10.0959 5.68298 10.245 5.68298C10.394 5.68298 10.537 5.74215 10.6425 5.84749C10.6953 5.90034 10.737 5.96318 10.7653 6.03234C10.7935 6.1015 10.8077 6.1756 10.807 6.25031C10.8063 6.32502 10.7908 6.39884 10.7612 6.46746C10.7317 6.53608 10.6888 6.59813 10.635 6.64999Z" fill="currentColor"></path>
                                </svg>
                            )}
                        </div>

                        {/* User Details */}
                        <div className='d-flex flex-row gap-6 align-items-center my-1'>
                            {/* For phone */}
                            <div className="d-flex gap-2 align-items-center">
                                <svg className="w-[16px] h-[16px] text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                    <path stroke="#71717B" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M18.427 14.768 17.2 13.542a1.733 1.733 0 0 0-2.45 0l-.613.613a1.732 1.732 0 0 1-2.45 0l-1.838-1.84a1.735 1.735 0 0 1 0-2.452l.612-.613a1.735 1.735 0 0 0 0-2.452L9.237 5.572a1.6 1.6 0 0 0-2.45 0c-3.223 3.2-1.702 6.896 1.519 10.117 3.22 3.221 6.914 4.745 10.12 1.535a1.601 1.601 0 0 0 0-2.456Z" />
                                </svg>
                                <a className="theme-link font-weight-600" rel="noreferrer" href={`tel:${userDetails?.user?.phone}`}>
                                    {userDetails?.user?.phone || 'N/A'}
                                </a>
                            </div>

                            {/* For email */}
                            <div className="d-flex gap-2 align-items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#71717B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path>
                                    <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                                </svg>
                                <a className="theme-link font-weight-600" rel="noreferrer" href={`mailto:${userDetails?.user?.email}`}>
                                    {userDetails?.user?.email || 'N/A'}
                                </a>
                            </div>

                            {/* For location */}
                            {/* <div className="d-flex gap-2 align-items-center">
                                <svg className="w-[16px] h-[16px] text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
                                    <path stroke="#71717B" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                                    <path stroke="#71717B" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17.8 13.938h-.011a7 7 0 1 0-11.464.144h-.016l.14.171c.1.127.2.251.3.371L12 21l5.13-6.248c.194-.209.374-.429.54-.659l.13-.155Z" />
                                </svg>
                                <a className="theme-link font-weight-600" rel="noopener noreferrer" target='_blank'
                                    href="https://www.google.com/maps/place/SF,+Bay+Area/data=!4m2!3m1!1s0x808583a3a688d7b5:0x8c891b8457461fa9?sa=X&ved=1t:155783&ictx=111">
                                    SF, Bay Area
                                </a>
                            </div> */}


                            {/* For role */}
                            <div className="d-flex gap-2 align-items-center">
                                <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
                                    <path stroke="#71717B" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M14.6144 7.19994c.3479.48981.5999 1.15357.5999 1.80006 0 1.6569-1.3432 3-3 3-1.6569 0-3.00004-1.3431-3.00004-3 0-.67539.22319-1.29865.59983-1.80006M6.21426 6v4m0-4 6.00004-3 6 3-6 2-2.40021-.80006M6.21426 6l3.59983 1.19994M6.21426 19.8013v-2.1525c0-1.6825 1.27251-3.3075 2.95093-3.6488l3.04911 2.9345 3-2.9441c1.7026.3193 3 1.9596 3 3.6584v2.1525c0 .6312-.5373 1.1429-1.2 1.1429H7.41426c-.66274 0-1.2-.5117-1.2-1.1429Z" />
                                </svg>

                                <span className="text-dark font-weight-600">
                                    {userDetails?.user?.user_type === 'participant'
                                        ? 'Participant'
                                        : userDetails?.user?.user_type === 'support_worker'
                                            ? 'Support Worker'
                                            : userDetails?.user?.user_type === 'support_coordinator_lac'
                                                ? 'Support Coordinator (LAC)'
                                                : userDetails?.user?.user_type === 'allied_health'
                                                    ? 'Allied Health Professional'
                                                    : userDetails?.user?.user_type === 'service_provider'
                                                        ? 'Service Provider'
                                                        : userDetails?.user?.user_type === 'ndis_business'
                                                            ? 'NDIS Business'
                                                            : userDetails?.user?.user_type === 'council'
                                                                ? 'Council'
                                                                : 'Nurse'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Extracted tabs header and content into UserOverviewTabs component */}
                <UserOverviewTabs userDetails={userDetails} saveUser={saveUser} formatError={formatError} />

            </KTCardBody>
        </KTCard>
    )
}

export default UserDetails
